import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useParams } from 'react-router-dom'

import { useTheme } from 'styled-components'

import { Divider, Text, ToastContext } from '@reveni-io/reveni-ui'

import SaveChangesButtons from '@/common/components/SaveChangesButtons/SaveChangesButtons'
import UnsavedChangesAlert from '@/common/components/UnsavedChangesAlert/UnsavedChangesAlert'

import AllowTransactionsConfiguration from './AllowTransactionsConfiguration/AllowTransactionsConfiguration'
import ExceptionDetailSkeleton from './ExceptionDetailSkeleton/ExceptionDetailSkeleton'
import ExceptionNotification from './ExceptionNotification/ExceptionNotification'
import useException from './hooks/useException/useException'
import OrderConfiguration from './OrderConfiguration/OrderConfiguration'
import OrderInformation from './OrderInformation/OrderInformation'
import PolicyRuleConfiguration from './PolicyRuleConfiguration/PolicyRuleConfiguration'
import { errorTypes } from './constants'
import * as Styles from './styles'

const ExceptionDetailConfiguration = () => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { id } = useParams()
  const location = useLocation()
  const { setToastInfo } = useContext(ToastContext)
  const {
    exception,
    hasChanges,
    revertChanges,
    creatingException,
    editingException,
    errors,
    isGiftCardEnabled,
    isStoreCreditEnabled,
    handleChange,
    handleChangeExpiresDate,
    onSaveChanges,
    fetchingReturnPortalFeatures,
    policyRulesData,
    gettingPolicyRules,
  } = useException({ selectedException: location.state?.selectedException, setToastInfo, id })

  const isFormValid = () => {
    const hasNoErrors = errors && Object.keys(errors).length === 0

    return exception?.order_number && exception?.expires_at && hasNoErrors
  }

  if (fetchingReturnPortalFeatures) return <ExceptionDetailSkeleton />

  return (
    <Styles.MainContainer>
      {hasChanges && <UnsavedChangesAlert />}
      <Styles.MainInfoContainer>
        <OrderInformation
          exception={exception}
          handleChange={handleChange}
          handleChangeExpiresDate={handleChangeExpiresDate}
          errors={errors}
        />
      </Styles.MainInfoContainer>
      <Divider />
      <Styles.EffectsContainer id={errorTypes.effectsEmpty}>
        <OrderConfiguration
          exception={exception}
          handleChange={handleChange}
          isStoreCreditEnabled={isStoreCreditEnabled}
          isGiftCardEnabled={isGiftCardEnabled}
        />
        <Divider />
        <AllowTransactionsConfiguration exception={exception} handleChange={handleChange} />
        <Divider />
        <PolicyRuleConfiguration
          exception={exception}
          handleChange={handleChange}
          policyRulesData={policyRulesData}
          loading={gettingPolicyRules}
        />
        {errors?.[errorTypes.effectsEmpty] && (
          <Text type="bodySRegular" color={theme.colors.danger.default}>
            {t('Marca al menos una opción para la excepción')}
          </Text>
        )}
      </Styles.EffectsContainer>
      <Divider />
      <ExceptionNotification exception={exception} handleChange={handleChange} />
      <SaveChangesButtons
        hasChanges={hasChanges}
        onSave={onSaveChanges}
        onRevertChanges={revertChanges}
        loading={creatingException || editingException}
        disabled={creatingException || editingException || !isFormValid()}
      />
    </Styles.MainContainer>
  )
}

export default ExceptionDetailConfiguration
